import { useQuery } from "@apollo/client";
import { Route, Routes, useParams } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useHasModuleAdminAccess from "shared/Module/access/useHasModuleAdminAccess";
import { Module } from "types/generated";

import ModuleSettingsAvailability from "./Availability";
import ModuleSettingsBehavior from "./Behavior";
import ModuleSettingsRouteLayout from "./components/RouteLayout";
import { ModuleSettingsContext } from "./Context";
import ModuleSettingsDeletion from "./Deletion";
import ModuleSettingsDetails from "./Details";
import { GET_MODULE_SETTINGS } from "./gql";
import ModuleSettingsIntegrations from "./Integrations";
import ModuleSettingsIntegrationsWebhook from "./Integrations/Webhook";
import ModuleSettingsIntegrationsWebhookDelivery from "./Integrations/WebhookDelivery";
import ModuleSettingsSharingNew from "./SharingNew";
import ModuleSettingsSourceCode from "./SourceCode";

const ModuleSettings = () => {
  const { moduleId } = useParams<{ moduleId: string }>();

  const { data, error, loading } = useQuery<{ module: Module }>(GET_MODULE_SETTINGS, {
    variables: {
      id: moduleId,
    },
    fetchPolicy: "no-cache",
  });

  const ErrorContent = useErrorHandle(error);

  const canManageModule = useHasModuleAdminAccess()(data?.module);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.module) {
    return <PageLoading />;
  }

  if (!data?.module) {
    return <NotFoundPage />;
  }

  return (
    <ModuleSettingsContext.Provider value={{ module: data.module, canManageModule }}>
      <Routes>
        <Route path="integrations/webhook/:webhookId/delivery/:deliveryId">
          <Route index path="*" element={<ModuleSettingsIntegrationsWebhookDelivery />} />
        </Route>
        <Route
          path="integrations/webhook/:webhookId"
          element={<ModuleSettingsIntegrationsWebhook />}
        />
        <Route element={<ModuleSettingsRouteLayout />}>
          <Route index element={<ModuleSettingsDetails />} />
          <Route path="source-code" element={<ModuleSettingsSourceCode />} />
          <Route path="behavior" element={<ModuleSettingsBehavior />} />
          <Route path="integrations">
            <Route index path="*" element={<ModuleSettingsIntegrations />} />
          </Route>
          <Route path="availability" element={<ModuleSettingsAvailability />} />
          <Route path="sharing" element={<ModuleSettingsSharingNew />} />
          {canManageModule.result && <Route path="deletion" element={<ModuleSettingsDeletion />} />}
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ModuleSettingsContext.Provider>
  );
};

export default ModuleSettings;
